import ClassNames from 'classnames'
import { PostCard } from 'components/molecules'
import { debounce } from 'lodash'
import * as React from 'react'
import styled from 'styled-components'
import { checkLikeable } from 'utils'
import I18n from '../../core/i18n'
import * as constants from '../../static/constants'
import Pagination from '../atoms/Pagination'
import Spinner from '../atoms/Spinner'

const urlParams = new URLSearchParams(window.location.search)

interface IPostListProps {
  showDescription?: boolean
  showMap?: boolean
  loading?: boolean
  isSignedIn: boolean
  favorite: boolean
  posts: any
  meta: any
  hasMap?: boolean
  noPostsTitle?: string
  keyword?: null | string
  user: any
  small?: boolean
  handleOnMouseEnter?(markerId): void
  handleOnMouseLeave?(): void
  getPostsByPage?(page: number): void
  title(count: number): string
  toggleLike(postId: number, like: boolean): void
}

const PostList: React.FC<IPostListProps> = props => {
  const [mapMode, setMapMode] = React.useState(false)

  React.useEffect(() => {
    const resizeHandler = debounce(() => {
      if (window.matchMedia(`(max-width: ${constants.BREAKPOINT_DESKTOP}px)`).matches) {
        setMapMode(false)
      } else {
        setMapMode(true)
      }
      return
    }, 100)
    window.addEventListener('resize', resizeHandler)

    return () => window.removeEventListener('resize', resizeHandler)
  }, [])
  const posts = props.small ? props.posts.filter((_, index) => index < 5) : props.posts
  const isMobile = matchMedia(`(max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px)`).matches

  return (
    <PostListWrapper className={ClassNames('PostList', { mapMode, hideMap: !props.showMap })}>
      {props.favorite && (
        <h1 className="PostList_FavoriteHeader">{I18n.t('post.favorite_post_heading')}</h1>
      )}
      <div className="PostList_Main">
        {props.loading ? (
          <Spinner />
        ) : (
          <div className="PostList_Inner">
            <div className="PostList_Header">
              {props.keyword && (
                <p className="PostList_Keyword">
                  {I18n.t('post.show_results_of', { keyword: props.keyword })}
                </p>
              )}
              <p className="PostList_Count">
                {!props.meta || !props.posts.length ? (
                  <span className="Wrapper_NoResult">
                    {props.noPostsTitle
                      ? props.noPostsTitle
                      : props.favorite
                      ? I18n.t('favorite.no_favorites')
                      : I18n.t('post.no_search_result')}
                  </span>
                ) : (
                  !props.favorite && <span>{props.title(props.meta.total_count)}</span>
                )}
              </p>
              {/* {props.posts.length > 0 && (
                <div className="PostList_Sort">
                  <span>Sort </span>
                  <OrderSelect>
                    <select
                      defaultValue={getCurrentSortColumn()}
                      onChange={event => changeOrder(event.target.value, getCurrentSortDirection())}
                    >
                      <option value="name">Name</option>
                      {constants.PAYMENT_REQUIRED && <option value="price">Price</option>}
                    </select>
                    <span />
                  </OrderSelect>
                  <span onClick={() => changeOrder(getCurrentSortColumn(), 'asc')}>↓ Asc</span>
                  <span onClick={() => changeOrder(getCurrentSortColumn(), 'desc')}>↑ Desc</span>
                </div>
              )} */}
            </div>
            <div className="PostList_Content">
              {posts.map((post, index) => (
                <PostCard
                  key={index}
                  post={post}
                  likeable={checkLikeable(post, props.user)}
                  column={isMobile}
                  noMargin={true}
                />
              ))}
            </div>
            {!props.small && (!props.meta || props.meta.total_pages <= 1) ? null : (
              <Pagination
                onChangePageHandler={props.getPostsByPage}
                currentPage={Number(props.meta.current_page)}
                prevPage={props.meta.prev_page}
                nextPage={props.meta.next_page}
                totalPages={props.meta.total_pages}
                totalCount={props.meta.total_count}
              />
            )}
          </div>
        )}
      </div>
    </PostListWrapper>
  )
}

const PostListWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    display: block;
  }
  .PostList_Main {
    width: 100%;
    padding: 8px 0 72px;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      position: relative;
      display: block;
      width: 100%;
      background-color: ${constants.COLORS.white};
      z-index: 900;
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      padding: 8px 16px 72px;
    }
  }

  &.mapMode {
    .PostList_Main {
      @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
        display: none;
      }
    }
  }

  .PostList_FavoriteHeader {
    padding: 0;
    font-size: 22px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      padding: 0 16px;
    }
  }

  .PostList_Keyword {
    font-size: 18px;
    margin-bottom: 16px;
  }

  .PostList_Count {
    font-size: 18px;
  }

  .PostList_Header {
    margin: 8px 0;
  }

  .PostList_Sort {
    display: flex;
    align-items: center;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      display: none;
    }
  }
  .PostList_Content {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -10px;
    > a {
      width: calc(25% - 20px);
      margin: 10px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
        width: calc(33.33% - 20px);
      }
      @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
        width: calc(50% - 20px);
      }
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        width: 100%;
        margin: 10px 0;
      }
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      flex-direction: column;
      margin: 0;
    }
  }

  &.hideMap {
    .PostList_Main {
      display: block;
      width: 100%;
    }

    .PostList_Inner {
      max-width: 1520px;
      margin: auto;
    }
  }

  &.hideMap.mapMode {
    .PostList_Main {
      @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
        display: none;
      }
    }
  }

  .Pagination {
    height: 48px;
  }
`

const OrderSelect = styled.div`
  position: relative;
  border: solid 1px ${constants.COLORS.white};
  margin-left: 12px;
  border-radius: 3px;

  > select {
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    border-radius: 0;
    border: 0;
    margin: 0;
    padding: 0;
    background: none transparent;
    vertical-align: middle;
    color: inherit;
    box-sizing: content-box;
    outline: none;

    width: 120px;
    padding: 2px 8px;
    font-size: 16px;
    font-weight: normal;
    cursor: pointer;
    background-color: ${constants.COLORS.white};
  }

  > span {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 9px;
    margin: auto;
    display: inline-block;
    vertical-align: middle;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #4f5959;
  }
`

const Map = styled.div`
  display: block;
  position: relative;
  flex: 1;
  height: 100%;
  width: 100%;
  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    position: absolute;
    top: 0;
    left: 0;
  }

  #map {
    height: 100%;
    width: 100%;
  }
`

export default PostList
